import { Camera as BJSCamera } from "@babylonjs/core/Cameras/camera";
import { PickingInfo } from "@babylonjs/core/Collisions/pickingInfo";
import { Ray } from "@babylonjs/core/Culling/ray";
import { Constants as BJSConstants } from "@babylonjs/core/Engines/constants";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { Matrix, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Viewport } from "@babylonjs/core/Maths/math.viewport";
import { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { InstancedLinesMesh, LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PassPostProcess } from "@babylonjs/core/PostProcesses/passPostProcess";
import { PostProcess } from "@babylonjs/core/PostProcesses/postProcess";
import { FxaaPostProcess } from "@babylonjs/core/PostProcesses/fxaaPostProcess";
import { SSAO2RenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssao2RenderingPipeline";
import "@babylonjs/core/Rendering/geometryBufferRendererSceneComponent"; //side effect for SSAO2RenderingPipeline
import "@babylonjs/core/Rendering/prePassRendererSceneComponent"; //side effect for SSAO2RenderingPipeline (only if forceGeometryBuffer = false)
import { removeElement } from "../../utils/array";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { intersectRayPlane, isPointInFrustum, isTriangleInFrustum, projectPointOnLine3ToRef, triangleNormalToRef } from "../utils/MathUtils";
import { Camera } from "./Camera";
import { CameraControl } from "./CameraControl";
export var ViewLayerMaskFlags = {
    None: 0x00000000,
    Screenshot: 0x00000001,
    All: 0xFFFFFFFF
};
export var CoordinatesType = {
    Client: 1,
    Viewer: 2
};
var View = /** @class */ (function () {
    function View(pViewer) {
        var _this = this;
        this.onBeforeCameraRender = function (pCamera) {
            if (pCamera !== _this.mCamera)
                return;
            _this.setupMeshVisibility();
        };
        this.onAfterCameraRender = function (pCamera) {
            if (pCamera !== _this.mCamera)
                return;
            _this.restoreMeshVisibility();
        };
        this.mViewer = pViewer;
        this.mSizeOverride = null;
        this.pixelScaleFactor = 1.0;
        this.mSSAO = null;
        this.mCamera = new Camera(this);
        this.mViewer.scene.onBeforeCameraRenderObservable.add(this.onBeforeCameraRender);
        this.mViewer.scene.onAfterCameraRenderObservable.add(this.onAfterCameraRender);
        this.mCamera.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        //create a div which is used as an html overlay
        this.mOverlayDiv = document.createElement("div");
        this.mOverlayDiv.className = "wcf-core wcf-view";
        this.mOverlayDiv.setAttribute("touch-action", "none"); //required for jquery.PEP
        this.mViewer.container.appendChild(this.mOverlayDiv);
        this.mCameraControl = new CameraControl(this.mViewer, this);
        this.setupPostProcessing();
    }
    Object.defineProperty(View.prototype, "viewer", {
        get: function () {
            return (this.mViewer);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "camera", {
        get: function () {
            return (this.mCamera);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "cameraControl", {
        get: function () {
            return (this.mCameraControl);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "overlayDiv", {
        get: function () {
            return (this.mOverlayDiv);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "width", {
        get: function () {
            return (this.mSizeOverride != null ? this.mSizeOverride.x : this.mViewer.width);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "height", {
        get: function () {
            return (this.mSizeOverride != null ? this.mSizeOverride.y : this.mViewer.height);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "renderWidth", {
        get: function () {
            return (this.mSizeOverride != null ? this.mSizeOverride.x : this.mViewer.canvas.width);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(View.prototype, "renderHeight", {
        get: function () {
            return (this.mSizeOverride != null ? this.mSizeOverride.y : this.mViewer.canvas.height);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Should be called after Viewer.removeView() was called and the view won't be used again.
     */
    View.prototype.dispose = function () {
        this.mViewer.scene.onBeforeCameraRenderObservable.removeCallback(this.onBeforeCameraRender);
        this.mViewer.scene.onAfterCameraRenderObservable.removeCallback(this.onAfterCameraRender);
        this.mCamera.dispose();
        if (this.mOverlayDiv != null)
            this.mViewer.container.removeChild(this.mOverlayDiv);
    };
    View.prototype.resize = function () {
        //update aspect ratio of ortho cameras
        this.updateOrthoAspectRatio();
        //resize the overlay div
        if (this.mOverlayDiv != null) {
            this.mOverlayDiv.style.width = this.width.toString() + "px";
            this.mOverlayDiv.style.height = this.height.toString() + "px";
            this.mOverlayDiv.style.left = "0px";
            this.mOverlayDiv.style.bottom = "0px";
            this.mCameraControl._updateCSSPerspective();
        }
        this.mCameraControl.notifyChange(); //update camera matrix
    };
    View.prototype.pickScene = function (pPos, pCoordType, pPredicate) {
        if (pPos == null)
            return (null);
        //pick expects viewer coordinates
        var tPos = gTmpVector3_1;
        this.convertCoordsToRef(pPos, pCoordType, CoordinatesType.Viewer, tPos);
        this.setupMeshVisibility();
        var tPick = this.mViewer.scene.pick(tPos.x, tPos.y, pPredicate, false, this.mCamera);
        this.restoreMeshVisibility();
        return (tPick);
    };
    View.prototype.pickSceneWithFrustum = function (pPos, pWidth, pHeight, pCoordType, pPredicate) {
        //pick expects viewer coordinates
        var tPos = this.convertCoords(pPos, pCoordType, CoordinatesType.Viewer);
        if (tPos == null)
            return (null);
        var tPickingInfo = null;
        var tFrustum = this.createPickingFrustum(tPos, pWidth, pHeight, CoordinatesType.Viewer);
        this.createPickingRayToRef(tPos, CoordinatesType.Viewer, null, gTmpRay);
        this.setupMeshVisibility();
        for (var _i = 0, _a = this.viewer.scene.meshes; _i < _a.length; _i++) {
            var tMesh = _a[_i];
            if (pPredicate != null) {
                if (!pPredicate(tMesh)) {
                    continue;
                }
            }
            else if (!tMesh.isEnabled() || !tMesh.isVisible || !tMesh.isPickable) {
                continue;
            }
            var tResult = this.pickMeshWithFrustum(tMesh, gTmpRay, tFrustum);
            if (!tResult || !tResult.hit)
                continue;
            if (tPickingInfo != null && tResult.distance >= tPickingInfo.distance)
                continue;
            tPickingInfo = tResult;
        }
        this.restoreMeshVisibility();
        return (tPickingInfo);
    };
    View.prototype.createPickingRay = function (pPos, pCoordType, pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        var tResult = Ray.Zero();
        this.createPickingRayToRef(pPos, pCoordType, pWorldMatrix, tResult);
        return (tResult);
    };
    View.prototype.createPickingRayToRef = function (pPos, pCoordType, pWorldMatrix, pResult) {
        //createPickingRay expects viewer coordinates
        this.convertCoordsToRef(pPos, pCoordType, CoordinatesType.Viewer, gTmpVector3_1);
        this.mViewer.scene.createPickingRayToRef(gTmpVector3_1.x, gTmpVector3_1.y, pWorldMatrix == null ? View.sIdentityMatrix : pWorldMatrix, pResult, this.mCamera, false);
    };
    View.prototype.createPickingFrustum = function (pPos, pWidth, pHeight, pCoordType) {
        if (pPos == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        var getPlane = function (pRay1, pRay2) {
            var tDir = pRay1.origin.subtract(pRay2.origin);
            tDir.normalize();
            var tNormal = Vector3.Cross(tDir, pRay1.direction);
            return (Plane.FromPositionAndNormal(pRay1.origin, tNormal));
        };
        var tNear = this.camera.minZ;
        var tFar = this.camera.maxZ;
        var tCamPos = this.cameraControl.position;
        var tCamDir = this.cameraControl.direction;
        //picking rect
        var tHW = pWidth * 0.5;
        var tHH = pHeight * 0.5;
        var tViewerPos = this.convertCoords(pPos, pCoordType, CoordinatesType.Viewer);
        var tTopLeft = new Vector2(tViewerPos.x - tHW, tViewerPos.y - tHH);
        var tBottomRight = new Vector2(tViewerPos.x + tHW, tViewerPos.y + tHH);
        var tTopLeftRay = this.createPickingRay(tTopLeft, CoordinatesType.Viewer);
        var tTopRightRay = this.createPickingRay(new Vector2(tBottomRight.x, tTopLeft.y), CoordinatesType.Viewer);
        var tBottomLeftRay = this.createPickingRay(new Vector2(tTopLeft.x, tBottomRight.y), CoordinatesType.Viewer);
        var tBottomRightRay = this.createPickingRay(tBottomRight, CoordinatesType.Viewer);
        var tPlanes = new Array();
        //near
        tPlanes.push(Plane.FromPositionAndNormal(tCamPos.add(tCamDir.scale(tNear)), tCamDir));
        //far
        tPlanes.push(Plane.FromPositionAndNormal(tCamPos.add(tCamDir.scale(tFar)), tCamDir.negate()));
        //left
        tPlanes.push(getPlane(tTopLeftRay, tBottomLeftRay));
        //right
        tPlanes.push(getPlane(tBottomRightRay, tTopRightRay));
        //top
        tPlanes.push(getPlane(tTopRightRay, tTopLeftRay));
        //bottom
        tPlanes.push(getPlane(tBottomLeftRay, tBottomRightRay));
        return (tPlanes);
    };
    /**
     * Projects a point from 3d world space to 2d view/viewer space and returns it as Vector2.
     */
    View.prototype.projectPoint2 = function (pPos, pTargetCoordsType, pWorldMatrix, pCheckIfBehindCamera) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        if (pCheckIfBehindCamera === void 0) { pCheckIfBehindCamera = true; }
        var tPoint = this.internalProjectPoint(pPos, pWorldMatrix, pCheckIfBehindCamera);
        if (tPoint == null)
            return (null);
        //convert to target coordinates
        var t2dPoint = new Vector2(tPoint.x, tPoint.y);
        this.convertCoordsToRef(t2dPoint, CoordinatesType.Viewer, pTargetCoordsType, t2dPoint);
        return (t2dPoint);
    };
    /**
     * Projects a point from 3d world space to 2d view/viewer space and returns it as Vector3.
     */
    View.prototype.projectPoint3 = function (pPos, pTargetCoordsType, pWorldMatrix, pCheckIfBehindCamera) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        if (pCheckIfBehindCamera === void 0) { pCheckIfBehindCamera = true; }
        var tPoint = this.internalProjectPoint(pPos, pWorldMatrix, pCheckIfBehindCamera);
        if (tPoint == null)
            return (null);
        //convert to target coordinates
        this.convertCoordsToRef(tPoint, CoordinatesType.Viewer, pTargetCoordsType, tPoint);
        return (tPoint);
    };
    /**
     * Unprojects a point from 2d view/viewer space to 3d world space.
     */
    View.prototype.unprojectPoint = function (pPos, pSourceCoordsType, pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        var tResult = Vector3.Zero();
        this.unprojectPointToRef(pPos, pSourceCoordsType, pWorldMatrix, tResult);
        return (tResult);
    };
    /**
     * Unprojects a point from 2d view/viewer space to 3d world space.
     */
    View.prototype.unprojectPointToRef = function (pPos, pSourceCoordsType, pWorldMatrix, pResult) {
        if (pPos == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        pResult.copyFrom(pPos);
        this.convertCoordsToRef(pPos, pSourceCoordsType, CoordinatesType.Viewer, pResult);
        Vector3.UnprojectToRef(pResult, this.width, this.height, pWorldMatrix == null ? View.sIdentityMatrix : pWorldMatrix, this.mCamera.getViewMatrix(), this.mCamera.getProjectionMatrix(), pResult);
    };
    /**
     * Projects the points p1 and p2 from world coordinates to viewer coordinates and returns its distance (pixel distance).
     */
    View.prototype.projectedDistance = function (pP1, pP2, pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        if (pP1 == null || pP2 == null)
            return (null);
        var tProjP1 = this.projectPoint2(pP1, CoordinatesType.Viewer, pWorldMatrix);
        if (tProjP1 == null)
            return (null);
        var tProjP2 = this.projectPoint2(pP2, CoordinatesType.Viewer, pWorldMatrix);
        if (tProjP2 == null)
            return (null);
        return (Vector2.Distance(tProjP1, tProjP2));
    };
    View.prototype.convertCoords = function (pCoords, pFrom, pTo) {
        var tCoords = Vector2.Zero();
        this.convertCoordsToRef(pCoords, pFrom, pTo, tCoords);
        return (tCoords);
    };
    View.prototype.convertCoordsToRef = function (pCoords, pFrom, pTo, pTarget) {
        pTarget.x = pCoords.x;
        pTarget.y = pCoords.y;
        if (pFrom === pTo)
            return;
        if (pFrom < pTo) {
            if (pFrom <= CoordinatesType.Client && pTo >= CoordinatesType.Viewer)
                this.mViewer.clientToViewerCoordsToRef(pTarget, pTarget);
        }
        else {
            if (pFrom >= CoordinatesType.Client && pTo <= CoordinatesType.Client)
                this.mViewer.viewerToClientCoordsToRef(pTarget, pTarget);
        }
    };
    View.prototype.serialize = function () {
        var tIOView = {
            cameraSettings: this.cameraControl.serialize()
        };
        return tIOView;
    };
    View.prototype.deserialize = function (pIOView) {
        if (pIOView != null) {
            this.cameraControl.deserialize(pIOView.cameraSettings);
            return (true);
        }
        return (false);
    };
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    View._CreateView = function (pViewer) {
        return (new View(pViewer));
    };
    /** @internal */
    View._GetLocalFrustum = function (pFrustum, pNode) {
        var tInvertedWorldMatrix = Matrix.Invert(pNode.getWorldMatrix());
        var tLocalFrustum = new Array();
        for (var _i = 0, pFrustum_1 = pFrustum; _i < pFrustum_1.length; _i++) {
            var tPlane = pFrustum_1[_i];
            tLocalFrustum.push(tPlane.transform(tInvertedWorldMatrix));
        }
        return (tLocalFrustum);
    };
    /** @internal */
    View.prototype._prepareRenderFrame = function (pMultiSamplingStep, pMaxSteps) {
        this.mMultiSamplingStep = pMultiSamplingStep;
        if (this.mMultiSamplingStep >= 0) {
            //enable MSAASamplingPass if Step > 0
            if (this.mMultiSamplingStep === 0 && this.mMSAASamplingPassAttached) {
                this.detachPostProcess(this.mMSAASamplingPass);
                this.mMSAASamplingPassAttached = false;
            }
            if (this.mMultiSamplingStep > 0 && !this.mMSAASamplingPassAttached) {
                this.attachPostProcess(this.mMSAASamplingPass);
                this.mMSAASamplingPassAttached = true;
            }
            //switch between MSAA result passes
            if (this.mMultiSamplingStep % 2 === 0) {
                this.attachPostProcess(this.mMSAAResultPass2);
                this.mMSAAResultPass2Attached = true;
            }
            else {
                this.attachPostProcess(this.mMSAAResultPass1);
                this.mMSAAResultPass1Attached = true;
            }
            //modify projection matrix for MSAA
            var tSampleOffset = View.sSamplingOffsets[this.mMultiSamplingStep];
            this.mCamera._setProjectionOffset(tSampleOffset.x / this.renderWidth, tSampleOffset.y / this.renderHeight);
            //disable FXAA
            if (this.mFXAAAttached) {
                this.detachPostProcess(this.mFXAA);
                this.mFXAAAttached = false;
            }
            //use max samples for SSAO
            if (this.mSSAO != null && this.mSSAOAttached && this.mSSAO.samples !== this.mSSAOMaxSamples) {
                this.mSSAO.samples = this.mSSAOMaxSamples;
            }
        }
        else {
            if (this.mMSAASamplingPassAttached) {
                this.detachPostProcess(this.mMSAASamplingPass);
                this.mMSAASamplingPassAttached = false;
            }
            if (this.mMSAAResultPass1Attached) {
                this.detachPostProcess(this.mMSAAResultPass1);
                this.mMSAAResultPass1Attached = false;
            }
            if (this.mMSAAResultPass2Attached) {
                this.detachPostProcess(this.mMSAAResultPass2);
                this.mMSAAResultPass2Attached = false;
            }
            //enable FXAA
            if (!this.mFXAAAttached && this.mViewer.fxaaEnabled) {
                this.attachPostProcess(this.mFXAA);
                this.mFXAAAttached = true;
            }
            //use min samples for SSAO
            if (this.mSSAO != null && this.mSSAOAttached && this.mSSAO.samples !== this.mSSAOMinSamples) {
                this.mSSAO.samples = this.mSSAOMinSamples;
            }
        }
        //update shadow generators
        for (var _i = 0, _a = this.mViewer.app.rendering.shadowGenerators; _i < _a.length; _i++) {
            var tGen = _a[_i];
            tGen.prepareRenderFrame(this.mMultiSamplingStep, pMaxSteps);
        }
    };
    /** @internal */
    View.prototype._finalizeRenderFrame = function (pMultiSamplingStep, pMaxSteps) {
        if (this.mMultiSamplingStep >= 0) {
            if (this.mMultiSamplingStep % 2 === 0) {
                this.detachPostProcess(this.mMSAAResultPass2);
                this.mMSAAResultPass2Attached = false;
            }
            else {
                this.detachPostProcess(this.mMSAAResultPass1);
                this.mMSAAResultPass1Attached = false;
            }
            //reset projection matrix
            this.mCamera._setProjectionOffset(0, 0);
        }
        //update shadow generators
        for (var _i = 0, _a = this.mViewer.app.rendering.shadowGenerators; _i < _a.length; _i++) {
            var tGen = _a[_i];
            tGen.finalizeRenderFrame(this.mMultiSamplingStep, pMaxSteps);
        }
    };
    /** @internal */
    View.prototype._setSizeOverride = function (pSize, pTriggerChangeEvent) {
        this.mSizeOverride = pSize;
        if (this.mSizeOverride != null) {
            this.mCamera._setFixedAspectRatio(this.mSizeOverride.x / this.mSizeOverride.y);
        }
        else
            this.mCamera._setFixedAspectRatio(null);
        this.updateOrthoAspectRatio();
        this.mCamera.notifyProjectionChanged();
        this.mCameraControl.notifyChange(pTriggerChangeEvent); //reset transfom matrix
    };
    /** @internal */
    View.prototype._getSizeOverride = function () {
        return (this.mSizeOverride);
    };
    /** @internal */
    View.prototype._setSSAOQuality = function (pQuality) {
        switch (pQuality) {
            case "Medium":
                this.mSSAOMinSamples = 16;
                this.mSSAOMaxSamples = 32;
                break;
            case "High":
                this.mSSAOMinSamples = 32;
                this.mSSAOMaxSamples = 32;
                break;
            default: //Low/Off
                this.mSSAOMinSamples = 12;
                this.mSSAOMaxSamples = 16;
                break;
        }
        if (!SSAO2RenderingPipeline.IsSupported) {
            return;
        }
        var tWasToggledOnOff = false;
        if (pQuality === "Off" && this.mSSAOAttached) {
            this.mViewer.scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline("SSAO", this.mCamera);
            this.mSSAOAttached = false;
            tWasToggledOnOff = true;
        }
        else if (pQuality !== "Off" && !this.mSSAOAttached) {
            this.mViewer.scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline("SSAO", this.mCamera);
            this.mSSAOAttached = true;
            tWasToggledOnOff = true;
        }
        //fix postprocesses, this seems to be a side effect of our attach/detachPostProcess workaround
        if (tWasToggledOnOff) {
            this.mMSAAResultPass1.markTextureDirty();
            this.mMSAAResultPass2.markTextureDirty();
            this.mMSAASamplingPass.markTextureDirty();
            this.mFXAA.markTextureDirty();
        }
        this.mViewer.requestRenderFrame();
    };
    /** @internal */
    View.prototype._updateFXAA = function () {
        if (!this.mViewer.fxaaEnabled && this.mFXAAAttached) {
            this.detachPostProcess(this.mFXAA);
            this.mFXAAAttached = false;
        }
        this.mViewer.requestRenderFrame();
    };
    View.prototype.setupMeshVisibility = function () {
        //process camera mode mask
        for (var _i = 0, _a = this.mViewer.scene.meshes; _i < _a.length; _i++) {
            var tMesh = _a[_i];
            tMesh._wcf_core_wasVisible = tMesh.isVisible;
            if (tMesh.isVisible) {
                var tMask = CameraControl.GetMeshCameraModeMask(tMesh);
                var tMaskHidden = tMask != null && (tMask & this.mCameraControl._camerModeMask) === 0;
                var tHiddenInScreenshot = this.mViewer._renderOnlyScreenshotMeshes && !this.mViewer._isScreenshotMesh(tMesh);
                if (tMaskHidden || tHiddenInScreenshot) {
                    //overwrite visibility in this case
                    tMesh.isVisible = false;
                }
            }
        }
    };
    View.prototype.restoreMeshVisibility = function () {
        for (var _i = 0, _a = this.mViewer.scene.meshes; _i < _a.length; _i++) {
            var tMesh = _a[_i];
            tMesh.isVisible = tMesh._wcf_core_wasVisible;
        }
    };
    View.prototype.updateOrthoAspectRatio = function () {
        if (this.mCamera.fovMode === BJSCamera.FOVMODE_VERTICAL_FIXED) {
            var tAspect = this.renderWidth / this.renderHeight;
            var tOrtho = this.mCamera.orthoTop;
            this.mCamera.orthoBottom = -tOrtho;
            this.mCamera.orthoLeft = -tOrtho * tAspect;
            this.mCamera.orthoRight = tOrtho * tAspect;
        }
        else {
            var tAspect = this.renderHeight / this.renderWidth;
            var tOrtho = this.mCamera.orthoRight;
            this.mCamera.orthoLeft = -tOrtho;
            this.mCamera.orthoTop = tOrtho * tAspect;
            this.mCamera.orthoBottom = -tOrtho * tAspect;
        }
        this.mCamera.notifyProjectionChanged();
    };
    View.prototype.internalProjectPoint = function (pPos, pWorldMatrix, pCheckIfBehindCamera) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        if (pCheckIfBehindCamera === void 0) { pCheckIfBehindCamera = true; }
        if (pPos == null)
            return (null);
        var tPoint = Vector3.Project(pPos, pWorldMatrix == null ? View.sIdentityMatrix : pWorldMatrix, this.mCameraControl.transformMatrix, View.sProjectViewport);
        if (pCheckIfBehindCamera && (tPoint.z < 0 || tPoint.z > 1))
            return (null);
        //viewport coords to view
        tPoint.x *= this.width;
        tPoint.y *= this.height;
        return (tPoint);
    };
    View.prototype.setupPostProcessing = function (pSetupSSAO) {
        var _this = this;
        if (pSetupSSAO === void 0) { pSetupSSAO = true; }
        var tPPSize = new ViewPostProcessSize(this);
        this.mMSAASamplingPass = new PostProcess("MSAASampling", "wcf.core.MSAA", ["amount"], ["lastFrameSampler"], tPPSize, this.mCamera, BJSConstants.TEXTURE_NEAREST_NEAREST);
        this.mMSAASamplingPass.onApplyObservable.add(function (pEffect) {
            if (_this.mMultiSamplingStep % 2 === 0)
                pEffect.setTextureFromPostProcess("lastFrameSampler", _this.mMSAAResultPass1);
            else
                pEffect.setTextureFromPostProcess("lastFrameSampler", _this.mMSAAResultPass2);
            var tAmount = 1.0 / (_this.mMultiSamplingStep + 1);
            pEffect.setFloat("amount", tAmount);
        });
        this.mMSAAResultPass1 = new PassPostProcess("MSAAResultPass1", tPPSize, this.mCamera, BJSConstants.TEXTURE_NEAREST_NEAREST);
        this.mMSAAResultPass2 = new PassPostProcess("MSAAResultPass2", tPPSize, this.mCamera, BJSConstants.TEXTURE_NEAREST_NEAREST);
        this.detachPostProcess(this.mMSAASamplingPass);
        this.detachPostProcess(this.mMSAAResultPass1);
        this.detachPostProcess(this.mMSAAResultPass2);
        this.mMSAASamplingPassAttached = false;
        this.mMSAAResultPass1Attached = false;
        this.mMSAAResultPass2Attached = false;
        this.mMultiSamplingStep = 0;
        //FXAA
        this.mFXAA = new FxaaPostProcess("FXAA", 1.0, this.mCamera);
        this.detachPostProcess(this.mFXAA);
        this.mFXAAAttached = false;
        if (pSetupSSAO) {
            //SSAO
            this.mSSAOMinSamples = 16;
            this.mSSAOMaxSamples = 32;
            this.mSSAOAttached = false;
            if (SSAO2RenderingPipeline.IsSupported) {
                this.mSSAO = new SSAO2RenderingPipeline("SSAO", this.mViewer.scene, { ssaoRatio: 0.25, blurRatio: 0.5 }, [this.mCamera], false);
                this.mSSAO.expensiveBlur = false;
                this.mSSAO.samples = this.mSSAOMinSamples;
                this.mSSAO.radius = 0.5;
                this.mSSAO.totalStrength = 0.5;
                this.mSSAOAttached = true;
            }
        }
    };
    View.prototype.attachPostProcess = function (pPosProcess) {
        //Operate directly on the array here.
        //Using Camera.attachPostProcess() will invalidate the post process texture which
        //leads to recreation of the texture for every MSAA frame.
        this.mCamera._postProcesses.push(pPosProcess);
    };
    View.prototype.detachPostProcess = function (pPosProcess) {
        //Operate directly on the array here.
        //Using Camera.detachPostProcess() will invalidate the post process texture which
        //leads to recreation of the texture for every MSAA frame.
        removeElement(this.mCamera._postProcesses, pPosProcess);
    };
    View.prototype.pickMeshWithFrustum = function (pMesh, pRay, pFrustum) {
        if (pMesh == null || pFrustum == null)
            return (null);
        if (!((pMesh instanceof Mesh && pMesh.geometry != null) || pMesh instanceof InstancedMesh))
            return (null);
        var tFoundDistance = null;
        var tPickedPoint = null;
        var tBoundingInfo = pMesh.getBoundingInfo();
        if (tBoundingInfo != null && tBoundingInfo.isInFrustum(pFrustum)) {
            if (!pMesh._generatePointsArray())
                return (null);
            var tMWorld = pMesh.getWorldMatrix();
            var tMInvWorld = Matrix.Invert(tMWorld);
            var tPositions = pMesh._positions;
            if (tPositions == null)
                return (null);
            //get local ray
            var tLocalRay = Ray.Transform(pRay, tMInvWorld);
            //get local frustum
            var tLocalFrustum = View._GetLocalFrustum(pFrustum, pMesh);
            var tIndices = pMesh.getIndices();
            if (tIndices == null)
                return (null);
            if (pMesh instanceof LinesMesh || pMesh instanceof InstancedLinesMesh) {
                //line mesh
                for (var i = 0; i < tIndices.length; i += 2) {
                    var tP1 = tPositions[tIndices[i]];
                    var tP2 = tPositions[tIndices[i + 1]];
                    //TODO: calculate correct threshold based on frustum size
                    var tDist = tLocalRay.intersectionSegment(tP1, tP2, pMesh.intersectionThreshold);
                    if (tDist >= 0 && (tFoundDistance == null || tDist < tFoundDistance)) {
                        tFoundDistance = tDist;
                    }
                }
            }
            else {
                //triangle mesh
                for (var i = 0; i < tIndices.length; i += 3) {
                    var tP1 = tPositions[tIndices[i]];
                    var tP2 = tPositions[tIndices[i + 1]];
                    var tP3 = tPositions[tIndices[i + 2]];
                    //check if triangle is in frustum
                    if (!isTriangleInFrustum(tP1, tP2, tP3, tLocalFrustum))
                        continue;
                    //check direct hit first
                    var tIsecInfo = tLocalRay.intersectsTriangle(tP1, tP2, tP3);
                    if (tIsecInfo != null) {
                        if (tFoundDistance == null || tIsecInfo.distance < tFoundDistance) {
                            tFoundDistance = tIsecInfo.distance;
                            tPickedPoint = null;
                        }
                    }
                    else {
                        //get plane of the triangle and intersect with ray
                        var tNormal = gTmpVector3_1;
                        triangleNormalToRef(tP1, tP2, tP3, tNormal);
                        var tPlane = Plane.FromPositionAndNormal(tP1, tNormal);
                        var tPlaneIsec = intersectRayPlane(tLocalRay, tPlane);
                        if (tPlaneIsec != null) {
                            var tClosest = gTmpVector3_2;
                            var tClosest2 = gTmpVector3_3;
                            //get point on triangle which is closest to the plane intersection point
                            projectPointOnLine3ToRef(tPlaneIsec, tP1, tP2, true, tClosest);
                            var tDist = Vector3.DistanceSquared(tClosest, tPlaneIsec);
                            //check second edge
                            projectPointOnLine3ToRef(tPlaneIsec, tP2, tP3, true, tClosest2);
                            var tDist2 = Vector3.DistanceSquared(tClosest2, tPlaneIsec);
                            if (tDist2 < tDist) {
                                tClosest = tClosest2;
                                tDist2 = tDist;
                            }
                            //check third edge
                            projectPointOnLine3ToRef(tPlaneIsec, tP3, tP1, true, tClosest2);
                            tDist2 = Vector3.DistanceSquared(tClosest2, tPlaneIsec);
                            if (tDist2 < tDist)
                                tClosest = tClosest2;
                            //check if found point is inside the frustum
                            if (isPointInFrustum(tClosest, tLocalFrustum)) {
                                var tDistance = Vector3.Distance(tClosest, tLocalRay.origin);
                                if (tFoundDistance == null || tDistance < tFoundDistance) {
                                    tFoundDistance = tDistance;
                                    tPickedPoint = tClosest;
                                }
                            }
                        }
                    }
                }
            }
            if (tFoundDistance != null) {
                var tPickingInfo = new PickingInfo();
                tPickingInfo.hit = true;
                tPickingInfo.pickedMesh = pMesh;
                //get picked point as world coordinates
                if (tPickedPoint == null) {
                    var tDirection = tLocalRay.direction.scale(tFoundDistance);
                    var tWorldDirection = Vector3.TransformNormal(tDirection, tMWorld);
                    tPickingInfo.pickedPoint = pRay.origin.add(tWorldDirection);
                }
                else {
                    tPickingInfo.pickedPoint = Vector3.TransformCoordinates(tPickedPoint, tMWorld);
                }
                //get world distance
                tPickingInfo.distance = Vector3.Distance(pRay.origin, tPickingInfo.pickedPoint);
                return (tPickingInfo);
            }
        }
        return (null);
    };
    //// private ///////////////////////////////////////////////////////////////////////////////
    View.sProjectViewport = new Viewport(0, 0, 1, 1);
    View.sIdentityMatrix = Matrix.Identity();
    View.sSamplingOffsets = [
        new Vector2(0, 0),
        new Vector2(0.7, 0.7),
        new Vector2(-0.7, 0.7),
        new Vector2(-0.7, -0.7),
        new Vector2(0.7, -0.7),
        new Vector2(0.6, 0.3),
        new Vector2(-0.6, 0.3),
        new Vector2(-0.6, -0.3),
        new Vector2(0.6, -0.3),
        new Vector2(0.3, 0.6),
        new Vector2(-0.3, 0.6),
        new Vector2(-0.3, -0.6),
        new Vector2(0.3, -0.6),
        new Vector2(0.5, 0.0),
        new Vector2(-0.5, 0.0),
        new Vector2(0.0, 0.5),
        new Vector2(0.0, -0.5),
        new Vector2(0.3, 0.3),
        new Vector2(-0.3, 0.3),
        new Vector2(-0.3, -0.3),
        new Vector2(0.3, -0.3),
        new Vector2(1.0, 1.0),
        new Vector2(-1.0, 1.0),
        new Vector2(-1.0, -1.0),
        new Vector2(1.0, -1.0),
        new Vector2(0.9, 0.2),
        new Vector2(-0.9, 0.2),
        new Vector2(-0.9, -0.2),
        new Vector2(0.9, -0.2),
        new Vector2(0.2, 0.9),
        new Vector2(-0.2, 0.9),
        new Vector2(-0.2, -0.9),
        new Vector2(0.2, -0.9)
    ];
    return View;
}());
export { View };
//helper class to setup correct post processing size
var ViewPostProcessSize = /** @class */ (function () {
    function ViewPostProcessSize(pView) {
        this.mView = pView;
    }
    Object.defineProperty(ViewPostProcessSize.prototype, "width", {
        get: function () {
            return (this.mView.renderWidth);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ViewPostProcessSize.prototype, "height", {
        get: function () {
            return (this.mView.renderHeight);
        },
        enumerable: false,
        configurable: true
    });
    return ViewPostProcessSize;
}());
//// temp variables ////////////////////////////////////////////////////////////////////////////
var gTmpVector3_1 = Vector3.Zero();
var gTmpVector3_2 = Vector3.Zero();
var gTmpVector3_3 = Vector3.Zero();
var gTmpRay = Ray.Zero();
//register MSAA shader
Effect.ShadersStore["wcf.core.MSAAFragmentShader"] = "\nprecision highp float;\n\nvarying vec2 vUV;\nuniform float amount;\nuniform sampler2D textureSampler;\nuniform sampler2D lastFrameSampler;\n\nvoid main(void)\n{\n    vec4 current = texture2D(textureSampler, vUV);\n    vec4 last = texture2D(lastFrameSampler, vUV);\n\n    gl_FragColor = last * (1.0 - amount) + current * amount;\n}";
